import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { it } from '../../services/api';
import Card from '../../components/common/Card';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { theme } from '../../theme';

const categoryIcons = {
  Laptop: '💻', Monitor: '🖥️', Phone: '📱', Keyboard: '⌨️',
  Mouse: '🖱️', Printer: '🖨️', default: '📦',
};

function SpecPill({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <span style={{ fontSize: '0.75rem', background: theme.pageBg, border: `1px solid ${theme.border}`, borderRadius: '0.375rem', padding: '0.25rem 0.5rem', color: theme.text, whiteSpace: 'nowrap' }}>
      <span style={{ color: theme.textMuted, fontWeight: 500 }}>{label}: </span>
      <strong>{value}</strong>
    </span>
  );
}

function WarrantyBadge({ expiry }) {
  if (!expiry) return null;
  const days = Math.ceil((new Date(expiry) - new Date()) / 86400000);
  if (days < 0) return <span style={{ fontSize: '0.72rem', fontWeight: 700, color: theme.danger, background: '#FEF2F2', padding: '0.2rem 0.5rem', borderRadius: '0.375rem' }}>Warranty Expired</span>;
  if (days <= 30) return <span style={{ fontSize: '0.72rem', fontWeight: 700, color: theme.danger, background: '#FEF2F2', padding: '0.2rem 0.5rem', borderRadius: '0.375rem' }}>Warranty: {days}d left</span>;
  if (days <= 90) return <span style={{ fontSize: '0.72rem', fontWeight: 700, color: theme.warning, background: '#FFFBEB', padding: '0.2rem 0.5rem', borderRadius: '0.375rem' }}>Warranty: {days}d left</span>;
  return <span style={{ fontSize: '0.72rem', fontWeight: 600, color: theme.success, background: '#F0FDF4', padding: '0.2rem 0.5rem', borderRadius: '0.375rem' }}>Warranty OK</span>;
}

function DetailRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.6rem 0', borderBottom: `1px solid ${theme.border}` }}>
      <span style={{ fontSize: '0.8rem', color: theme.textMuted, fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: '0.85rem', color: value ? theme.text : theme.textMuted, fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>{value || '—'}</span>
    </div>
  );
}

function MyAssets() {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState(null);

  useEffect(() => {
    it.getMyAssets().then((r) => { setAssets(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: '3rem', color: theme.textMuted }}>Loading...</div>;

  const handleRaiseTicket = (asset) => {
    navigate('/employee/tickets/new', { state: { preselect_asset_id: asset.asset_id } });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>My Assets</h1>
        <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>{assets.length} asset{assets.length !== 1 ? 's' : ''} assigned to you</p>
      </div>

      {assets.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '3rem 0' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💻</div>
            <p style={{ color: theme.textMuted }}>No assets assigned to you</p>
            <p style={{ fontSize: '0.8rem', color: theme.textMuted, marginTop: '0.5rem' }}>Contact IT admin to get an asset assigned.</p>
          </div>
        </Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '1rem' }}>
          {assets.map((asset) => {
            const icon = categoryIcons[asset.category] || categoryIcons.default;
            return (
              <div key={asset.asset_id}
                style={{ background: theme.white, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', display: 'flex', flexDirection: 'column' }}>
                {/* Card header */}
                <div style={{ padding: '1.125rem 1.25rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: '0.625rem', background: theme.pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.375rem', flexShrink: 0, border: `1px solid ${theme.border}` }}>
                    {icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{asset.name}</h3>
                    <p style={{ margin: '0.15rem 0 0', fontSize: '0.75rem', color: theme.textMuted }}>{asset.make} {asset.model}</p>
                  </div>
                  <Badge status="assigned" />
                </div>

                {/* Meta row */}
                <div style={{ padding: '0.75rem 1.25rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  {asset.asset_code && (
                    <code style={{ fontSize: '0.75rem', color: theme.textMuted, background: theme.pageBg, padding: '0.15rem 0.4rem', borderRadius: '0.25rem', border: `1px solid ${theme.border}` }}>{asset.asset_code}</code>
                  )}
                  <WarrantyBadge expiry={asset.warranty_expiry} />
                </div>

                {/* Spec pills */}
                {(asset.cpu || asset.ram_gb || asset.storage_gb || asset.os) && (
                  <div style={{ padding: '0.625rem 1.25rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    <SpecPill label="CPU" value={asset.cpu} />
                    <SpecPill label="RAM" value={asset.ram_gb ? `${asset.ram_gb}GB` : null} />
                    <SpecPill label="Storage" value={asset.storage_gb ? `${asset.storage_gb}GB` : null} />
                    <SpecPill label="OS" value={asset.os} />
                  </div>
                )}

                {/* Quick info */}
                <div style={{ padding: '0.75rem 1.25rem', flex: 1 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                    {asset.serial_number && (
                      <div>
                        <p style={{ fontSize: '0.68rem', color: theme.textMuted, margin: 0, fontWeight: 600, textTransform: 'uppercase' }}>Serial</p>
                        <p style={{ fontSize: '0.8rem', color: theme.text, margin: '0.1rem 0 0', fontFamily: 'monospace' }}>{asset.serial_number}</p>
                      </div>
                    )}
                    {asset.assigned_date && (
                      <div>
                        <p style={{ fontSize: '0.68rem', color: theme.textMuted, margin: 0, fontWeight: 600, textTransform: 'uppercase' }}>Since</p>
                        <p style={{ fontSize: '0.8rem', color: theme.text, margin: '0.1rem 0 0' }}>{new Date(asset.assigned_date + 'T12:00:00').toLocaleDateString()}</p>
                      </div>
                    )}
                    {asset.location && (
                      <div>
                        <p style={{ fontSize: '0.68rem', color: theme.textMuted, margin: 0, fontWeight: 600, textTransform: 'uppercase' }}>Location</p>
                        <p style={{ fontSize: '0.8rem', color: theme.text, margin: '0.1rem 0 0' }}>{asset.location}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action footer */}
                <div style={{ padding: '0.75rem 1.25rem', borderTop: `1px solid ${theme.border}`, display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <Button size="sm" variant="outlineGray" onClick={() => setSelectedAsset(asset)}>Details</Button>
                  <Button size="sm" variant="primary" onClick={() => handleRaiseTicket(asset)}>Raise Ticket</Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Asset Detail Modal */}
      {selectedAsset && (
        <Modal isOpen={!!selectedAsset} title={selectedAsset.name} onClose={() => setSelectedAsset(null)} maxWidth="480px">
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem 1rem', background: theme.pageBg, borderRadius: '0.5rem', marginBottom: '1.25rem' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '0.625rem', background: theme.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.6rem', border: `1px solid ${theme.border}` }}>
              {categoryIcons[selectedAsset.category] || categoryIcons.default}
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontWeight: 700, color: theme.text }}>{selectedAsset.name}</p>
              <p style={{ margin: '0.15rem 0 0', fontSize: '0.78rem', color: theme.textMuted }}>{selectedAsset.category} · {selectedAsset.make} {selectedAsset.model}</p>
            </div>
            <WarrantyBadge expiry={selectedAsset.warranty_expiry} />
          </div>

          <DetailRow label="Asset Code" value={selectedAsset.asset_code} />
          <DetailRow label="Serial Number" value={selectedAsset.serial_number} />
          <DetailRow label="Location" value={selectedAsset.location} />
          <DetailRow label="Assigned Date" value={selectedAsset.assigned_date ? new Date(selectedAsset.assigned_date + 'T12:00:00').toLocaleDateString() : null} />
          <DetailRow label="Warranty Expiry" value={selectedAsset.warranty_expiry ? new Date(selectedAsset.warranty_expiry + 'T12:00:00').toLocaleDateString() : null} />

          {/* Specs — always shown */}
          <div style={{ marginTop: '1rem' }}>
            <p style={{ fontSize: '0.72rem', color: theme.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' }}>Technical Specifications</p>
            <DetailRow label="CPU" value={selectedAsset.cpu} />
            <DetailRow label="RAM" value={selectedAsset.ram_gb ? `${selectedAsset.ram_gb} GB` : null} />
            <DetailRow label="Storage" value={selectedAsset.storage_gb ? `${selectedAsset.storage_gb} GB` : null} />
            <DetailRow label="Operating System" value={selectedAsset.os} />
            <DetailRow label="Screen Size" value={selectedAsset.screen_size} />
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
            <Button variant="outlineGray" onClick={() => setSelectedAsset(null)}>Close</Button>
            <Button variant="primary" onClick={() => { setSelectedAsset(null); handleRaiseTicket(selectedAsset); }}>Raise Ticket</Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default MyAssets;
